import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';

import { User } from './user';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private url = environment.backend + '/api/users';

  constructor(private http: HttpClient) { }

  getItems(): Observable<User[]> {
    return this.http.get<User[]>(this.url);
  }

  getItem(id: number): Observable<User> {
    const url = `${this.url}/${id}`;
    return this.http.get<User>(url);
  }

  getItemByUsernameOrEmail(identificator: string): Observable<User> {
    const url = `${this.url}/${identificator}/userdata`;
    return this.http.get<User>(url);
  }

  getAuthenticatedUser(identificator: string, password: string): Observable<User> {
    const url = `${this.url}/${identificator}/authenticate`;
    return this.http.get<User>(url);
  }

  getOriginalItemsByIdOrganisation(idOrganisation: number): Observable<User[]> {
    return this.http.get<User[]>(this.url + '/' + idOrganisation + '/original/byIdOrganisation', {
      headers: new HttpHeaders({ 'authorization': 'Bearer ' + localStorage.getItem('id_token') })
    });
  }

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(this.url, {
      headers: new HttpHeaders({ 'authorization': 'Bearer ' + localStorage.getItem('id_token') })
    });
  }

  getUser(id: number): Observable<User> {
    const url = `${this.url}/${id}`;
    return this.http.get<User>(url);
  }

  addUser(item: User): Observable<User> {
    return this.http.post<User>(this.url, item, httpOptions);
  }

  deleteUser(item: User | number): Observable<User> {
    const id = typeof item === 'number' ? item : item.id;
    const url = `${this.url}/${id}`;

    return this.http.delete<User>(url, httpOptions);
  }

  updateUser(item: User): Observable<any> {
    const url = `${this.url}/${item.id}`;
    return this.http.put<User>(url, item, httpOptions);
  }

  getCurrentUser(): Observable<any> {
    const token = localStorage.getItem('id_token');
    //console.log('Current User Token:', token);
    //console.log('Backend URL:', this.url);
    
    if (!token) {
      return throwError('No authentication token found');
    }

    const fullUrl = `${this.url}/profile`;
    //console.log('Full Request URL:', fullUrl);

    return this.http.get(fullUrl, {
      headers: new HttpHeaders({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      }),
      observe: 'response' // This will give us full response details
    }).pipe(
      catchError(error => {
        console.error('Get Current User Full Error:', {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          headers: error.headers,
          message: error.message,
          error: error.error
        });
        return throwError(error);
      })
    ).pipe(
      map(response => {
        //console.log('Response Status:', response.status);
        //console.log('Response Headers:', response.headers);
        return response.body;
      })
    );
  }

  updateProfile(userData: any): Observable<any> {
    const token = localStorage.getItem('id_token');
    console.log('Update Profile Token:', token);
    
    if (!token) {
      return throwError('No authentication token found');
    }

    return this.http.put(`${this.url}/profile`, userData, {
      headers: new HttpHeaders({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      })
    }).pipe(
      catchError(error => {
        console.error('Update Profile Error:', error);
        return throwError(error);
      })
    );
  }

  uploadAvatar(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('avatar', file);
    return this.http.post(`${this.url}/avatar`, formData);
  }

  sendPasswordResetEmail(email: string): Observable<any> {
    return this.http.post(`${this.url}/insertTokenToEmail`, { email }, httpOptions).pipe(
      catchError(error => {
        console.error('Send Password Reset Email Error:', error);
        return throwError(error);
      })
    );
  }
}

